// A MÉRT AZONOSÍTÓK választója — a hozzárendelés-űrlap ÉS a másolás-párbeszéd közös mezője.
//
// MIÉRT KELL: egy fióknak rendszerenként több külső azonosítója lehet (a HotelCMS-ben az egység egy domain —
// külön loyalty-, booking-, ticket-domain), ezért a használat-alapú tétel KIFEJEZETTEN megmondja, melyik
// azonosító mérése adja az összegét. Az opciók a fiók adatlapján felvett azonosítók, a megjegyzésükkel együtt —
// a megjegyzés (pl. a domain) az, ami alapján a felhasználó a helyeset kiválasztja.
//
// MIÉRT KÖZÖS KOMPONENS: a másolás-párbeszéd nem nyitja meg az űrlapot, de a cél-fiók más azonosítókat kínál,
// mint a forrásé — ugyanazt a kérdést ugyanúgy kell feltenni mindkét helyen.
import { useTranslation } from 'react-i18next';

import { EntityMultiSelect } from '@/components/shared/entity-multiselect';
import { type UsageRefOption } from '@/features/service-assignments/form';

export interface UsageRefsSelectProps {
  options: UsageRefOption[];
  value: string[];
  onChange: (keys: string[]) => void;
  /** Ügyfél-szintű tételnél több fiók azonosítója is szerepel → a fiók-nevet is kiírjuk. */
  showAccount: boolean;
  disabled?: boolean;
  error?: string | undefined;
}

export function UsageRefsSelect({
  options,
  value,
  onChange,
  showAccount,
  disabled = false,
  error,
}: UsageRefsSelectProps): JSX.Element {
  const { t } = useTranslation();
  const multiOptions = options.map((o) => {
    const id = `${t(`accounts.integrationSystem.${o.integration.system}`)} ${o.integration.externalId}`;
    const withNote = o.integration.note ? `${id} — ${o.integration.note}` : id;
    return { id: o.key, name: showAccount ? `${o.accountName} · ${withNote}` : withNote };
  });

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">
        {t('serviceAssignments.form.usageRefs')}
        <span className="text-destructive ml-0.5" aria-hidden="true">
          *
        </span>
      </label>
      <EntityMultiSelect
        options={multiOptions}
        value={value}
        onChange={onChange}
        disabled={disabled || options.length === 0}
        placeholder={t('serviceAssignments.form.usageRefsPlaceholder')}
        countLabel={(count) => t('serviceAssignments.form.usageRefsCount', { count })}
        aria-label={t('serviceAssignments.form.usageRefs')}
      />
      <p className="text-subtle text-xs">
        {options.length === 0
          ? t('serviceAssignments.form.usageRefsEmpty')
          : t('serviceAssignments.form.usageRefsHint')}
      </p>
      {error && <p className="text-destructive text-sm">{error}</p>}
    </div>
  );
}
